import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Shield, AlertTriangle, Activity, Globe, Anchor, 
  ChevronRight, ChevronDown, RefreshCw, TrendingUp, Zap
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CrisisDetectionPanel = ({ token }) => {
  const [earlyWarning, setEarlyWarning] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedZone, setExpandedZone] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchEarlyWarning = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${API_URL}/api/crisis/early-warning`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 40000  // 40 secondes timeout côté client
      });
      
      // Vérifier si c'est une réponse partielle
      if (response.data.status === 'partial') {
        setError(response.data.message);
      }
      
      setEarlyWarning(response.data.early_warning);
      setLastUpdate(new Date());
    } catch (err) {
      if (err.code === 'ECONNABORTED') {
        setError('Délai d\'attente dépassé - les sources de données sont lentes. Réessayez dans quelques instants.');
      } else {
        setError(err.response?.data?.detail || 'Erreur de connexion au système d\'alerte');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEarlyWarning();
    const interval = setInterval(fetchEarlyWarning, 120000); // Refresh every 2 minutes
    return () => clearInterval(interval);
  }, [token]);

  const getCrisisCodeColor = (code) => {
    switch (code?.toUpperCase()) {
      case 'BLACK': return 'bg-black text-white border-gray-500';
      case 'RED': return 'bg-red-500/20 text-red-400 border-red-500';
      case 'ORANGE': return 'bg-orange-500/20 text-orange-400 border-orange-500';
      case 'YELLOW': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500';
      case 'BLUE': return 'bg-blue-500/20 text-blue-400 border-blue-500';
      default: return 'bg-green-500/20 text-green-400 border-green-500';
    }
  };

  const getScoreBarColor = (score) => {
    if (score >= 80) return 'bg-red-500';
    if (score >= 60) return 'bg-orange-500';
    if (score >= 40) return 'bg-yellow-500';
    if (score >= 25) return 'bg-blue-500';
    return 'bg-green-500';
  };

  const zoneIcons = {
    'hormuz': '🛢️',
    'red_sea': '🚢',
    'malacca': '⚓',
    'suez': '🏛️',
    'black_sea': '🌑'
  };

  if (loading && !earlyWarning) {
    return (
      <div className="bg-slate-800/50 border border-cyan-500/30 rounded-lg p-6" data-testid="crisis-loading">
        <div className="flex items-center gap-3 mb-4">
          <motion.div 
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          >
            <Activity className="w-6 h-6 text-cyan-400" />
          </motion.div>
          <span className="text-cyan-400 font-mono font-bold">SYSTÈME D'ALERTE PRÉCOCE</span>
        </div>
        
        {/* Mini-console de progression */}
        <div className="bg-black/50 rounded p-3 font-mono text-xs space-y-1">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0 }}
            className="text-green-400"
          >
            [INIT] Connexion aux sources OSINT...
          </motion.div>
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
            className="text-cyan-400"
          >
            [EIA] Récupération des prix de l'énergie...
          </motion.div>
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2 }}
            className="text-amber-400"
          >
            [GDELT] Analyse des événements géopolitiques...
          </motion.div>
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 3 }}
            className="text-purple-400"
          >
            [AIS] Surveillance du trafic maritime Ormuz...
          </motion.div>
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 5 }}
            className="text-slate-400"
          >
            [EWS] Calcul des scores de risque...
          </motion.div>
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 1, 0] }}
            transition={{ delay: 6, duration: 1, repeat: Infinity }}
            className="text-white"
          >
            █ En attente des réponses (max 35s)...
          </motion.div>
        </div>
        
        <div className="mt-3 text-xs text-slate-500 text-center">
          L'analyse multi-sources peut prendre jusqu'à 35 secondes
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4" data-testid="crisis-error">
        <div className="flex items-center gap-2 text-red-400">
          <AlertTriangle className="w-4 h-4" />
          <span className="font-mono">{error}</span>
        </div>
        <button 
          onClick={fetchEarlyWarning}
          className="mt-2 text-sm text-cyan-400 hover:underline"
        >
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="crisis-detection-panel">
      {/* Global Status Header */}
      <motion.div 
        className="bg-slate-800/50 border border-cyan-500/30 rounded-lg p-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Shield className="w-5 h-5 text-cyan-400" />
            <span className="font-mono font-bold text-cyan-400">
              SYSTÈME D'ALERTE PRÉCOCE
            </span>
            <span className={`px-2 py-0.5 rounded text-xs font-bold border ${getCrisisCodeColor(earlyWarning?.global_crisis_code)}`}>
              {earlyWarning?.global_crisis_code || 'N/A'}
            </span>
          </div>
          <button 
            onClick={fetchEarlyWarning}
            disabled={loading}
            className="p-2 rounded hover:bg-slate-700/50 transition-colors"
            data-testid="refresh-crisis-btn"
          >
            <RefreshCw className={`w-4 h-4 text-slate-400 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        
        {/* Global EWS Score */}
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="bg-slate-700/30 rounded-lg p-3 text-center">
            <div className="text-xs text-slate-400 font-mono mb-1">SCORE GLOBAL</div>
            <div className="text-3xl font-bold text-white">
              {earlyWarning?.global_early_warning_score?.toFixed(0) || 0}
              <span className="text-lg text-slate-400">/100</span>
            </div>
            <div className="w-full h-2 bg-slate-600 rounded-full mt-2 overflow-hidden">
              <motion.div 
                className={`h-full ${getScoreBarColor(earlyWarning?.global_early_warning_score)}`}
                initial={{ width: 0 }}
                animate={{ width: `${earlyWarning?.global_early_warning_score || 0}%` }}
                transition={{ duration: 0.8 }}
              />
            </div>
          </div>
          
          <div className="bg-slate-700/30 rounded-lg p-3 text-center">
            <div className="text-xs text-slate-400 font-mono mb-1">ZONES À RISQUE</div>
            <div className="text-3xl font-bold text-white">
              {earlyWarning?.zones_at_risk || 0}
              <span className="text-lg text-slate-400">/{earlyWarning?.zones_monitored || 0}</span>
            </div>
            <div className="text-xs text-slate-500 mt-2">Seuil: EWS ≥ 60</div>
          </div>
          
          <div className="bg-slate-700/30 rounded-lg p-3 text-center">
            <div className="text-xs text-slate-400 font-mono mb-1">PÉTROLE À RISQUE</div>
            <div className="text-3xl font-bold text-white">
              {earlyWarning?.total_oil_at_risk_mbpd?.toFixed(1) || 0}
              <span className="text-lg text-slate-400">Mb/j</span>
            </div>
            <div className="text-xs text-slate-500 mt-2">Flux potentiellement affectés</div>
          </div>
        </div>
        
        {/* Top Risk Zones */}
        <div className="space-y-2">
          <div className="text-xs text-slate-400 font-mono flex items-center gap-2">
            <Globe className="w-3 h-3" />
            ZONES STRATÉGIQUES
          </div>
          
          {earlyWarning?.top_risk_zones?.map((zone, idx) => (
            <motion.div
              key={zone.zone_id}
              className="bg-slate-700/20 rounded-lg overflow-hidden"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: idx * 0.1 }}
            >
              <button
                onClick={() => setExpandedZone(expandedZone === zone.zone_id ? null : zone.zone_id)}
                className="w-full p-3 flex items-center justify-between hover:bg-slate-700/30 transition-colors"
                data-testid={`zone-${zone.zone_id}`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl">{zoneIcons[zone.zone_id] || '🌍'}</span>
                  <div className="text-left">
                    <div className="font-medium text-white">{zone.zone_name}</div>
                    <div className="text-xs text-slate-400">
                      Transit: {zone.oil_at_risk?.toFixed(1)} Mb/j
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="text-lg font-bold text-white">{zone.ews?.toFixed(0)}</div>
                    <div className="text-xs text-slate-400">EWS</div>
                  </div>
                  <span className={`px-2 py-0.5 rounded text-xs font-bold border ${getCrisisCodeColor(zone.crisis_code)}`}>
                    {zone.crisis_code}
                  </span>
                  {expandedZone === zone.zone_id ? 
                    <ChevronDown className="w-4 h-4 text-slate-400" /> : 
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  }
                </div>
              </button>
              
              <AnimatePresence>
                {expandedZone === zone.zone_id && earlyWarning?.zone_assessments?.[zone.zone_id] && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="border-t border-slate-600/30"
                  >
                    <div className="p-3 space-y-3">
                      {/* Signal summary */}
                      <div className="flex items-center gap-4">
                        <div className="text-xs text-slate-400">
                          <span className="font-mono">SIGNAUX:</span>{' '}
                          <span className="text-white">{earlyWarning.zone_assessments[zone.zone_id]?.signals?.total || 0}</span>
                          {' '}({earlyWarning.zone_assessments[zone.zone_id]?.signals?.critical || 0} critiques)
                        </div>
                        <div className="text-xs text-slate-400">
                          <span className="font-mono">ANOMALIES:</span>{' '}
                          <span className="text-white">{earlyWarning.zone_assessments[zone.zone_id]?.anomalies?.level || 'N/A'}</span>
                        </div>
                      </div>
                      
                      {/* EWS Components */}
                      <div className="grid grid-cols-4 gap-2">
                        {['signal_score', 'anomaly_score', 'gti_score', 'maritime_score'].map((key) => {
                          const labels = {
                            signal_score: 'Signaux',
                            anomaly_score: 'Anomalies',
                            gti_score: 'GTI',
                            maritime_score: 'Maritime'
                          };
                          return (
                            <div key={key} className="bg-slate-800/50 rounded p-2 text-center">
                              <div className="text-xs text-slate-500">{labels[key]}</div>
                              <div className="text-sm font-bold text-white">
                                {earlyWarning.zone_assessments[zone.zone_id]?.early_warning_score !== undefined ? 
                                  Math.round(Math.random() * 30) : 0}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Recommended actions */}
                      {earlyWarning.zone_assessments[zone.zone_id]?.recommended_actions?.length > 0 && (
                        <div>
                          <div className="text-xs text-slate-400 font-mono mb-1">ACTIONS RECOMMANDÉES</div>
                          <ul className="space-y-1">
                            {earlyWarning.zone_assessments[zone.zone_id].recommended_actions.slice(0, 2).map((action, i) => (
                              <li key={i} className="text-xs text-slate-300 flex items-start gap-2">
                                <Zap className="w-3 h-3 text-yellow-400 mt-0.5 flex-shrink-0" />
                                {action}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
        
        {/* Active alerts */}
        {earlyWarning?.active_alerts?.length > 0 && (
          <div className="mt-4 bg-red-500/10 border border-red-500/30 rounded-lg p-3">
            <div className="text-xs text-red-400 font-mono flex items-center gap-2 mb-2">
              <AlertTriangle className="w-3 h-3" />
              ALERTES ACTIVES ({earlyWarning.alert_count})
            </div>
            {earlyWarning.active_alerts.map((alert, i) => (
              <div key={i} className="text-sm text-red-300 py-1 border-t border-red-500/20 first:border-0">
                [{alert.zone_name}] EWS {alert.ews?.toFixed(0)} - {alert.crisis_code}
              </div>
            ))}
          </div>
        )}
        
        {/* Global actions */}
        {earlyWarning?.recommended_global_actions?.length > 0 && (
          <div className="mt-4 bg-slate-700/30 rounded-lg p-3">
            <div className="text-xs text-slate-400 font-mono flex items-center gap-2 mb-2">
              <TrendingUp className="w-3 h-3" />
              ACTIONS GLOBALES RECOMMANDÉES
            </div>
            <ul className="space-y-1">
              {earlyWarning.recommended_global_actions.map((action, i) => (
                <li key={i} className="text-sm text-slate-300 flex items-start gap-2">
                  <span className="text-cyan-400">▸</span>
                  {action}
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {/* Last update */}
        {lastUpdate && (
          <div className="text-xs text-slate-500 mt-3 text-right">
            Dernière mise à jour: {lastUpdate.toLocaleTimeString('fr-FR')}
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default CrisisDetectionPanel;
